<template>
    <div class="min-h-screen w-full flex flex-col bg-gradient-to-br from-gray-100 to-blue-50">
        <div class="h-full container mx-auto">
            <div class="flex flex-col h-full w-full p-8">
                <!-- Breadcrumb -->
                <nav class="text-sm text-gray-600 mb-6 flex">
                    <router-link to="/dashboard"
                        class="hover:text-blue-700 transition font-medium">Dashboard</router-link>
                    <span class="mx-1">/</span>
                    <span class="text-blue-900 font-semibold">Student Council</span>
                </nav>

                <!-- Page Title Card -->
                <div class="bg-white sm:rounded-3xl shadow-md p-4 sm:p-8 mb-4 sm:mb-10 sm:border border-gray-200">
                    <h1 class="text-xl sm:text-3xl font-bold text-blue-900 flex items-center gap-3">
                        🎓 Student Council
                    </h1>
                    <p class="text-gray-500 mt-2 text-xs sm:text-sm">Connect with your campus student council
                        representatives</p>
                </div>

                <!-- Statistics Section -->
                <div class="mb-8">
                    <div class="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
                        <!-- FCHS Campuses Card -->
                        <div
                            class="group relative bg-white rounded-2xl shadow-lg hover:shadow-2xl transition-all duration-500 transform hover:-translate-y-2 hover:scale-105 overflow-hidden border border-blue-100">
                            <div
                                class="absolute inset-0 bg-gradient-to-br from-blue-50 via-blue-50 to-white opacity-50 group-hover:opacity-100 transition-opacity duration-500">
                            </div>
                            <div class="relative p-8 text-center">
                                <div
                                    class="w-20 h-20 bg-gradient-to-br from-blue-500 to-blue-700 rounded-2xl flex items-center justify-center mx-auto mb-6 shadow-lg group-hover:shadow-xl group-hover:scale-110 transition-all duration-500 transform group-hover:rotate-0">
                                    <font-awesome-icon icon="university" class="w-10 h-10 text-white" />
                                </div>
                                <div
                                    class="text-5xl font-extrabold text-blue-600 mb-3 group-hover:text-blue-700 transition-colors duration-300">
                                    {{ getFCHSCount() }}
                                </div>
                                <div class="text-sm font-semibold text-gray-700 uppercase tracking-wider">FCHS Campuses
                                </div>
                                <div
                                    class="absolute bottom-0 left-0 right-0 h-1 bg-gradient-to-r from-blue-400 to-blue-600 transform scale-x-0 group-hover:scale-x-100 transition-transform duration-500">
                                </div>
                            </div>
                        </div>

                        <!-- ADPOLY Campuses Card -->
                        <div
                            class="group relative bg-white rounded-2xl shadow-lg hover:shadow-2xl transition-all duration-500 transform hover:-translate-y-2 hover:scale-105 overflow-hidden border border-teal-100">
                            <div
                                class="absolute inset-0 bg-gradient-to-br from-teal-50 via-teal-50 to-white opacity-50 group-hover:opacity-100 transition-opacity duration-500">
                            </div>
                            <div class="relative p-8 text-center">
                                <div
                                    class="w-20 h-20 bg-gradient-to-br from-teal-500 to-teal-700 rounded-2xl flex items-center justify-center mx-auto mb-6 shadow-lg group-hover:shadow-xl group-hover:scale-110 transition-all duration-500 transform group-hover:rotate-0">
                                    <font-awesome-icon icon="cogs" class="w-10 h-10 text-white" />
                                </div>
                                <div
                                    class="text-5xl font-extrabold text-teal-600 mb-3 group-hover:text-teal-700 transition-colors duration-300">
                                    {{ getADPOLYCount() }}
                                </div>
                                <div class="text-sm font-semibold text-gray-700 uppercase tracking-wider">ADPOLY
                                    Campuses</div>
                                <div
                                    class="absolute bottom-0 left-0 right-0 h-1 bg-gradient-to-r from-teal-400 to-teal-600 transform scale-x-0 group-hover:scale-x-100 transition-transform duration-500">
                                </div>
                            </div>
                        </div>

                        <!-- Total Council Members Card -->
                        <div
                            class="group relative bg-white rounded-2xl shadow-lg hover:shadow-2xl transition-all duration-500 transform hover:-translate-y-2 hover:scale-105 overflow-hidden border border-green-100">
                            <div
                                class="absolute inset-0 bg-gradient-to-br from-green-50 via-green-50 to-white opacity-50 group-hover:opacity-100 transition-opacity duration-500">
                            </div>
                            <div class="relative p-8 text-center">
                                <div
                                    class="w-20 h-20 bg-gradient-to-br from-green-500 to-green-700 rounded-2xl flex items-center justify-center mx-auto mb-6 shadow-lg group-hover:shadow-xl group-hover:scale-110 transition-all duration-500 transform group-hover:rotate-0">
                                    <font-awesome-icon icon="users" class="w-10 h-10 text-white" />
                                </div>
                                <div
                                    class="text-5xl font-extrabold text-green-600 mb-3 group-hover:text-green-700 transition-colors duration-300">
                                    {{ getTotalCouncilMembersCount() }}
                                </div>
                                <div class="text-sm font-semibold text-gray-700 uppercase tracking-wider">Total Council
                                    Members</div>
                                <div
                                    class="absolute bottom-0 left-0 right-0 h-1 bg-gradient-to-r from-green-400 to-green-600 transform scale-x-0 group-hover:scale-x-100 transition-transform duration-500">
                                </div>
                            </div>
                        </div>

                        <!-- Active Councils Card -->
                        <div
                            class="group relative bg-white rounded-2xl shadow-lg hover:shadow-2xl transition-all duration-500 transform hover:-translate-y-2 hover:scale-105 overflow-hidden border border-purple-100">
                            <div
                                class="absolute inset-0 bg-gradient-to-br from-purple-50 via-purple-50 to-white opacity-50 group-hover:opacity-100 transition-opacity duration-500">
                            </div>
                            <div class="relative p-8 text-center">
                                <div
                                    class="w-20 h-20 bg-gradient-to-br from-purple-500 to-purple-700 rounded-2xl flex items-center justify-center mx-auto mb-6 shadow-lg group-hover:shadow-xl group-hover:scale-110 transition-all duration-500 transform group-hover:rotate-0">
                                    <font-awesome-icon icon="handshake" class="w-10 h-10 text-white" />
                                </div>
                                <div
                                    class="text-5xl font-extrabold text-purple-600 mb-3 group-hover:text-purple-700 transition-colors duration-300">
                                    {{ studentCouncils.length }}
                                </div>
                                <div class="text-sm font-semibold text-gray-700 uppercase tracking-wider">Active
                                    Councils</div>
                                <div
                                    class="absolute bottom-0 left-0 right-0 h-1 bg-gradient-to-r from-purple-400 to-purple-600 transform scale-x-0 group-hover:scale-x-100 transition-transform duration-500">
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                <!-- Campus Widgets Grid -->
                <div v-if="isLoading" class="flex justify-center items-center py-12">
                    <base-page-spinner type="spin" class-list="h-20"></base-page-spinner>
                </div>
                <div v-else class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-8">
                    <div v-for="council in studentCouncils" :key="council.id"
                        class="bg-white rounded-2xl shadow-lg hover:shadow-xl transition-all duration-300 transform hover:-translate-y-1 overflow-hidden">
                        <div :class="`h-48 bg-gradient-to-br ${getHeaderGradient(council.campus_code)} relative`">
                            <div class="absolute inset-0 bg-black bg-opacity-20"></div>
                            <div class="absolute bottom-4 left-4 text-white">
                                <h3 class="text-2xl font-bold">{{ council.name }}</h3>
                                <p :class="`${getTextColor(council.campus_code)}`">{{ council.college_name }}</p>
                            </div>
                            <div class="absolute top-4 right-4">
                                <div
                                    class="w-12 h-12 bg-white bg-opacity-20 rounded-full flex items-center justify-center">
                                    <font-awesome-icon :icon="getIcon(council.entity_code)"
                                        class="w-6 h-6 text-white" />
                                </div>
                            </div>
                        </div>
                        <div class="p-6">
                            <div class="flex items-center justify-between mb-4">
                                <span
                                    :class="`text-sm font-medium ${getCategoryClass(council.campus_code)} px-3 py-1 rounded-full`">
                                    {{ council.category }}
                                </span>
                                <span class="text-sm text-gray-500">{{ council.status }}</span>
                            </div>
                            <p class="text-gray-600 mb-4">{{ council.description }}</p>
                            <div class="flex items-center justify-between">
                                <div class="flex items-center text-sm text-gray-500">
                                    <font-awesome-icon icon="users" class="w-4 h-4 mr-2" />
                                    <span>{{ council.members_count }} Members</span>
                                </div>
                                <button v-if="council.members_count > 0"
                                    @click.stop="openCouncilModal(council.entity_code, council.campus_code, council)"
                                    :class="`${getButtonClass(council.campus_code)} text-white px-4 py-2 rounded-lg hover:opacity-90 transition-colors text-sm font-medium`">
                                    View Council
                                </button>
                                <button v-else @click="showComingSoonAlert(council.name)"
                                    :class="`${getButtonClass(council.campus_code)} text-white px-4 py-2 rounded-lg hover:opacity-90 transition-colors text-sm font-medium`">
                                    View Council
                                </button>
                            </div>
                        </div>
                    </div>
                </div>

                <!-- Quick Actions -->
                <!-- <div class="bg-white rounded-2xl shadow-lg p-8">
                    <h2 class="text-2xl font-bold text-gray-900 mb-6 text-center">Quick Actions</h2>
                    <div class="grid grid-cols-1 md:grid-cols-3 gap-6">
                        <button
                            class="flex items-center justify-center p-6 bg-gradient-to-r from-blue-600 to-blue-700 text-white rounded-xl hover:from-blue-700 hover:to-blue-800 transition-all duration-300 transform hover:scale-105">
                            <font-awesome-icon icon="calendar-alt" class="w-6 h-6 mr-3" />
                            <span class="font-semibold">View Events</span>
                        </button>

                        <button
                            class="flex items-center justify-center p-6 bg-gradient-to-r from-green-600 to-green-700 text-white rounded-xl hover:from-green-700 hover:to-green-800 transition-all duration-300 transform hover:scale-105">
                            <font-awesome-icon icon="comments" class="w-6 h-6 mr-3" />
                            <span class="font-semibold">Join Discussion</span>
                        </button>

                        <button
                            class="flex items-center justify-center p-6 bg-gradient-to-r from-purple-600 to-purple-700 text-white rounded-xl hover:from-purple-700 hover:to-purple-800 transition-all duration-300 transform hover:scale-105">
                            <font-awesome-icon icon="envelope" class="w-6 h-6 mr-3" />
                            <span class="font-semibold">Contact Council</span>
                        </button>
                    </div>
                </div> -->
            </div>
        </div>

        <base-full-modal v-if="isShowPopUp" :title="getModalTitle()" @close="isShowPopUp = false">
            <div class="p-6">
                <!-- Council Members Grid -->
                <div v-if="isLoadingMembers" class="flex justify-center items-center py-12">
                    <base-page-spinner type="spin" class-list="h-20"></base-page-spinner>
                </div>
                <div v-else-if="currentCouncilMembers.length === 0" class="text-center py-12">
                    <div class="flex flex-col items-center">
                        <div class="w-24 h-24 bg-gray-100 rounded-full flex items-center justify-center mb-4">
                            <font-awesome-icon icon="users" class="w-12 h-12 text-gray-400" />
                        </div>
                        <p class="text-gray-600 text-lg font-medium">No members found for this council.</p>
                    </div>
                </div>
                <div v-else class="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4 sm:gap-6">
                    <div v-for="(member, index) in currentCouncilMembers" :key="index"
                        class="group relative bg-white rounded-2xl shadow-lg hover:shadow-2xl transition-all duration-300 transform hover:-translate-y-2 overflow-hidden border border-gray-100 flex flex-col">
                        <!-- Card Gradient Background -->
                        <div
                            :class="`absolute inset-0 bg-gradient-to-br ${getRoleGradient(member.role)} opacity-5 group-hover:opacity-10 transition-opacity duration-300`">
                        </div>

                        <div class="relative px-6 pt-6 pb-4 flex-1 flex flex-col">
                            <!-- Profile Picture Section -->
                            <div class="flex justify-center mb-4">
                                <div class="relative">
                                    <div
                                        class="absolute inset-0 bg-gradient-to-br from-blue-400 to-purple-500 rounded-full blur-xl opacity-50 group-hover:opacity-75 transition-opacity duration-300">
                                    </div>
                                    <div
                                        class="relative w-20 h-20 rounded-full ring-4 ring-white shadow-xl overflow-hidden bg-gradient-to-br from-blue-100 to-purple-100">
                                        <img :src="member.profile_picture_url || getStudentImage(member.student_id, selectedEntity)"
                                            :alt="member.name" class="w-full h-full object-cover"
                                            @error="handleImageError">
                                    </div>
                                    <!-- Status Indicator -->
                                    <div
                                        class="absolute bottom-0 right-0 w-6 h-6 bg-green-500 rounded-full border-4 border-white shadow-lg">
                                    </div>
                                </div>
                            </div>

                            <!-- Member Info Section -->
                            <div class="text-center flex-1 flex flex-col">
                                <!-- Name -->
                                <h3
                                    class="text-base font-bold text-gray-900 mb-1 group-hover:text-blue-600 transition-colors duration-300 line-clamp-2">
                                    {{ member.name || 'N/A' }}
                                </h3>

                                <!-- Student ID -->
                                <p class="text-xs text-gray-500 mb-4 font-mono">
                                    {{ member.student_id }}
                                </p>

                                <!-- Spacer to push role badge to bottom -->
                                <div class="flex-1"></div>

                                <!-- Role Badge - Fixed to bottom -->
                                <div class="w-full inline-flex items-center justify-center px-3 py-2 rounded-full text-xs font-semibold shadow-sm"
                                    :class="getRoleBadgeClass(member.role)">
                                    <font-awesome-icon :icon="getRoleIcon(member.role)" class="w-3 h-3 mr-1.5" />
                                    <span class="whitespace-nowrap">{{ member.role }}</span>
                                </div>
                            </div>
                        </div>

                        <!-- Decorative Bottom Border -->
                        <div
                            :class="`absolute bottom-0 left-0 right-0 h-1 bg-gradient-to-r ${getRoleGradient(member.role)}`">
                        </div>
                    </div>
                </div>
            </div>
        </base-full-modal>

    </div>
</template>

<script>
import gql from "graphql-tag";

const GET_STUDENT_COUNCILS = gql`
  query GetStudentCouncils($academic_year: String) {
    studentCouncils(academic_year: $academic_year) {
      error
      error_code
      message
      data {
        id
        name
        entity_code
        campus_code
        college_name
        category
        description
        academic_year
        status
        members_count
      }
    }
  }
`;

const GET_STUDENT_COUNCIL_MEMBERS = gql`
  query GetStudentCouncilMembers($council_id: ID, $entity_code: String, $campus_code: String, $academic_year: String) {
    studentCouncilMembers(council_id: $council_id, entity_code: $entity_code, campus_code: $campus_code, academic_year: $academic_year) {
      error
      error_code
      message
      data {
        student_id
        name
        role
        profile_picture_url
      }
    }
  }
`;

export default {
    name: "student-council",
    data() {
        return {
            isLoading: false,
            isShowPopUp: false,
            selectedEntity: null,
            selectedCampus: null,
            selectedCouncil: null,
            academicYear: '2025-2026',
            studentCouncils: [],
            currentCouncilMembers: [],
            isLoadingMembers: false,
        };
    },
    created() {
        this.fetchStudentCouncils();
    },
    methods: {
        async fetchStudentCouncils() {
            this.isLoading = true;
            try {
                const response = await this.$apollo.query({
                    query: GET_STUDENT_COUNCILS,
                    variables: {
                        academic_year: this.academicYear,
                    },
                    fetchPolicy: 'network-only',
                });

                if (response.data.studentCouncils.error) {
                    console.error("GraphQL Error:", response.data.studentCouncils.message);
                    return;
                }

                this.studentCouncils = response.data.studentCouncils.data || [];
            } catch (error) {
                console.error("Failed to load student councils:", error);
            } finally {
                this.isLoading = false;
            }
        },
        async fetchCouncilMembers(entityCode, campusCode) {
            this.isLoadingMembers = true;
            this.currentCouncilMembers = [];
            try {
                const variables = {
                    entity_code: entityCode,
                    campus_code: campusCode,
                    academic_year: this.academicYear,
                };

                // Only add council_id if selectedCouncil exists and has an id
                if (this.selectedCouncil && this.selectedCouncil.id) {
                    variables.council_id = this.selectedCouncil.id;
                }

                const response = await this.$apollo.query({
                    query: GET_STUDENT_COUNCIL_MEMBERS,
                    variables: variables,
                    fetchPolicy: 'network-only',
                });

                if (response.data && response.data.studentCouncilMembers) {
                    if (response.data.studentCouncilMembers.error) {
                        console.error("GraphQL Error:", response.data.studentCouncilMembers.message);
                        this.$swal({
                            icon: 'error',
                            title: 'Error',
                            text: response.data.studentCouncilMembers.message || 'Failed to load council members',
                        });
                        return;
                    }

                    this.currentCouncilMembers = response.data.studentCouncilMembers.data || [];
                }
            } catch (error) {
                console.error("Failed to load council members:", error);
                const errorMessage = error.graphQLErrors && error.graphQLErrors.length > 0
                    ? error.graphQLErrors[0].message
                    : error.message || 'Failed to load council members';

                this.$swal({
                    icon: 'error',
                    title: 'Error',
                    text: errorMessage,
                });
                this.currentCouncilMembers = [];
            } finally {
                this.isLoadingMembers = false;
            }
        },
        getStudentImage(studentId, entity) {
            const entityCode = entity || 'FCHS';
            return `https://www.iat.ac.ae/sis/Integrations/Get_Student_Image/Q4tphjXBanCMiro8Kpfbi1QEU5qZb01uWtpkQlpfet8/${studentId}/${entityCode}`;
        },
        openCouncilModal(entityCode, campusCode, council) {
            this.selectedEntity = entityCode;
            this.selectedCampus = campusCode;
            this.selectedCouncil = council;
            this.isShowPopUp = true;
            this.fetchCouncilMembers(entityCode, campusCode);
        },
        getModalTitle() {
            if (this.selectedCouncil) {
                return `Student Council For ${this.selectedCouncil.name} Academic Year ${this.selectedCouncil.academic_year}`;
            }
            if (this.selectedEntity && this.selectedCampus) {
                const campusName = this.selectedCampus.replace('FCHS-', '').replace('ADPOLY-', '').replace('-', ' ');
                const campusDisplayName = campusName.split(' ').map(word => {
                    return word.charAt(0).toUpperCase() + word.slice(1).toLowerCase();
                }).join(' ');
                return `Student Council For ${this.selectedEntity} ${campusDisplayName} Academic Year ${this.academicYear}`;
            }
            return 'Student Council';
        },
        getHeaderGradient(campusCode) {
            const gradients = {
                'FCHS-ALAIN': 'from-blue-500 to-blue-700',
                'FCHS-MAFRAQ': 'from-green-500 to-green-700',
                'FCHS-AJMAN': 'from-purple-500 to-purple-700',
                'FCHS-DHAFRA': 'from-orange-500 to-orange-700',
                'AD': 'from-teal-500 to-teal-700',
                'AA': 'from-indigo-500 to-indigo-700',
            };
            return gradients[campusCode] || 'from-gray-500 to-gray-700';
        },
        getTextColor(campusCode) {
            const colors = {
                'FCHS-ALAIN': 'text-blue-100',
                'FCHS-MAFRAQ': 'text-green-100',
                'FCHS-AJMAN': 'text-purple-100',
                'FCHS-DHAFRA': 'text-orange-100',
                'AD': 'text-teal-100',
                'AA': 'text-indigo-100',
            };
            return colors[campusCode] || 'text-gray-100';
        },
        getCategoryClass(campusCode) {
            const classes = {
                'FCHS-ALAIN': 'text-blue-600 bg-blue-100',
                'FCHS-MAFRAQ': 'text-green-600 bg-green-100',
                'FCHS-AJMAN': 'text-purple-600 bg-purple-100',
                'FCHS-DHAFRA': 'text-orange-600 bg-orange-100',
                'AD': 'text-teal-600 bg-teal-100',
                'AA': 'text-indigo-600 bg-indigo-100',
            };
            return classes[campusCode] || 'text-gray-600 bg-gray-100';
        },
        getButtonClass(campusCode) {
            const classes = {
                'FCHS-ALAIN': 'bg-blue-600',
                'FCHS-MAFRAQ': 'bg-green-600',
                'FCHS-AJMAN': 'bg-purple-600',
                'FCHS-DHAFRA': 'bg-orange-600',
                'AD': 'bg-teal-600',
                'AA': 'bg-indigo-600',
            };
            return classes[campusCode] || 'bg-gray-600';
        },
        getIcon(entityCode) {
            return entityCode === 'ADPOLY' ? 'cogs' : 'university';
        },
        handleImageError(event) {
            // Fallback to a default avatar or placeholder
            event.target.src = 'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iODAiIGhlaWdodD0iODAiIHZpZXdCb3g9IjAgMCA4MCA4MCIgZmlsbD0ibm9uZSIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj4KPGNpcmNsZSBjeD0iNDAiIGN5PSI0MCIgcj0iNDAiIGZpbGw9IiNFNUU3RUIiLz4KPHN2ZyB4PSIyMCIgeT0iMjAiIHdpZHRoPSI0MCIgaGVpZ2h0PSI0MCIgdmlld0JveD0iMCAwIDI0IDI0IiBmaWxsPSJub25lIj4KPHBhdGggZD0iTTEyIDEyQzE0LjIwOTEgMTIgMTYgMTAuMjA5MSAxNiA4QzE2IDUuNzkwODYgMTQuMjA5MSA0IDEyIDRDOS43OTA4NiA0IDggNS43OTA4NiA4IDhDOCAxMC4yMDkxIDkuNzkwODYgMTIgMTIgMTJaIiBmaWxsPSIjOUNBM0FGIi8+CjxwYXRoIGQ9Ik0xMiAxNEM5LjMzIDE0IDcgMTUuMzMgNyAxOFYyMEgxN1YxOEMxNyAxNS4zMyAxNC42NyAxNCAxMiAxNFoiIGZpbGw9IiM5Q0EzQUYiLz4KPC9zdmc+Cjwvc3ZnPgo=';
        },
        showComingSoonAlert(campusName) {
            this.$swal({
                title: 'Coming Soon!',
                text: `${campusName} student council information will be available soon.`,
                icon: 'info',
                confirmButtonText: 'OK',
                confirmButtonColor: '#3B82F6',
                customClass: {
                    popup: 'rounded-2xl',
                    title: 'text-gray-900 font-bold',
                    content: 'text-gray-600',
                    confirmButton: 'rounded-lg font-semibold'
                }
            });
        },
        getTotalCouncilMembersCount() {
            return this.studentCouncils.reduce((total, council) => {
                return total + (council.members_count || 0);
            }, 0);
        },
        getFCHSCount() {
            return this.studentCouncils.filter(council => council.entity_code === 'FCHS').length;
        },
        getADPOLYCount() {
            return this.studentCouncils.filter(council => council.entity_code === 'ADPOLY').length;
        },
        getRoleGradient(role) {
            const roleLower = role.toLowerCase();
            if (roleLower.includes('president')) {
                return 'from-yellow-400 to-orange-500';
            } else if (roleLower.includes('vice')) {
                return 'from-blue-400 to-indigo-500';
            } else if (roleLower.includes('secretary') || roleLower.includes('treasurer')) {
                return 'from-green-400 to-emerald-500';
            } else if (roleLower.includes('coordinator')) {
                return 'from-purple-400 to-pink-500';
            } else if (roleLower.includes('representative')) {
                return 'from-teal-400 to-cyan-500';
            } else {
                return 'from-gray-400 to-gray-500';
            }
        },
        getRoleBadgeClass(role) {
            const roleLower = role.toLowerCase();
            if (roleLower.includes('president')) {
                return 'bg-gradient-to-r from-yellow-50 to-orange-50 text-orange-700 border border-orange-200';
            } else if (roleLower.includes('vice')) {
                return 'bg-gradient-to-r from-blue-50 to-indigo-50 text-blue-700 border border-blue-200';
            } else if (roleLower.includes('secretary') || roleLower.includes('treasurer')) {
                return 'bg-gradient-to-r from-green-50 to-emerald-50 text-green-700 border border-green-200';
            } else if (roleLower.includes('coordinator')) {
                return 'bg-gradient-to-r from-purple-50 to-pink-50 text-purple-700 border border-purple-200';
            } else if (roleLower.includes('representative')) {
                return 'bg-gradient-to-r from-teal-50 to-cyan-50 text-teal-700 border border-teal-200';
            } else {
                return 'bg-gradient-to-r from-gray-50 to-slate-50 text-gray-700 border border-gray-200';
            }
        },
        getRoleIcon(role) {
            const roleLower = role.toLowerCase();
            if (roleLower.includes('president')) {
                return 'crown';
            } else if (roleLower.includes('vice')) {
                return 'star';
            } else if (roleLower.includes('secretary')) {
                return 'file-alt';
            } else if (roleLower.includes('treasurer')) {
                return 'dollar-sign';
            } else if (roleLower.includes('coordinator')) {
                return 'calendar-alt';
            } else if (roleLower.includes('representative')) {
                return 'user-tie';
            } else if (roleLower.includes('relation')) {
                return 'comments';
            } else {
                return 'user';
            }
        }
    }
};
</script>

<style scoped></style>
